<script setup lang="ts">
import { VForm } from 'vuetify/components/VForm'

import AuthProvider from '@/views/pages/authentication/AuthProvider.vue'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { userAbilityRulesStorage } from '@layouts/plugins/casl'
import { themeConfig } from '@themeConfig'

import authV2RegisterIllustrationBorderedDark from '@images/pages/auth-v2-register-illustration-bordered-dark.png'
import authV2RegisterIllustrationBorderedLight from '@images/pages/auth-v2-register-illustration-bordered-light.png'
import authV2RegisterIllustrationDark from '@images/pages/auth-v2-register-illustration-dark.png'
import authV2RegisterIllustrationLight from '@images/pages/auth-v2-register-illustration-light.png'
import authV2MaskDark from '@images/pages/misc-mask-dark.png'
import authV2MaskLight from '@images/pages/misc-mask-light.png'

const imageVariant = useGenerateImageVariant(authV2RegisterIllustrationLight,
  authV2RegisterIllustrationDark,
  authV2RegisterIllustrationBorderedLight,
  authV2RegisterIllustrationBorderedDark, true)

const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)

const route = useRoute('register')
const router = useRouter()

const provider = route.query.provider
const oauthId = route.query.oauthId
console.log('oauthId', oauthId)

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const ability = useAbility()

const form = ref({
  compNo: 'nstc',
  email: '',
  username: '',
  depNo: '',
  postNo: '',
  phone: '',
  password: '',
  chkPassword: '',
  privacyPolicies: false,
})

// 检查表单是否有效
const formIsValid = computed(() => 
  form.value.privacyPolicies && 
  form.value.password === form.value.chkPassword &&
  form.value.depNo &&
  form.value.postNo &&
  form.value.phone
);

const errors = ref<Record<string, string | undefined>>({
  email: undefined,
  password: undefined,
})

const refVForm = ref<VForm>()

const isPasswordVisible = ref(false)
const isChkPasswordVisible = ref(false)

const successMessage = ref('')
const errorMessage = ref('')

const register = async () => {
  try {
    const res = await $api('/du/user/userRegister/register', {
      method: 'POST',
      body: {
        compNo: form.value.compNo,
        email: form.value.email,
        username: form.value.username,
        password: form.value.password,
        depNo: form.value.depNo,
        postNo: form.value.postNo,
        phone: form.value.phone
      },
      onResponse(context) {
        console.log('context', context)
        // successMessage.value = '帳號註冊完成, 待審核通過後即可登入'
        if (context.response.status == 200) {
          $swal({
            title: '註冊完成<BR>待審核通過將會發送帳號至您的電子信箱',
            icon: 'success',
            confirmButtonColor: '#3085d6',
            confirmButtonText: '確定',
          }).then(() => {
            // location.href = location.origin
          });
        }
        else {
          $swal({
            title: '註冊失敗',
            text: context.response._data.message,
            icon: 'error',
            confirmButtonColor: '#3085d6',
            confirmButtonText: '確定',
          });
        }
          

        // setTimeout(() => {
        //   location.href = location.origin
        // }, 2000)
      },
      onResponseError({ response }) {
        errors.value = response._data.errors
        errorMessage.value = '帳號註冊失敗'
      },
    })

    const { data } = res
    //
    // 使用共享的 ref，確保與 isHidden 函數同步
    userAbilityRulesStorage.value = data.userAbilityRules
    ability.update(data.userAbilityRules)
    //
    useStorage('userData', {}).value = data.userInfo
    useCookie('userToken').value = data.userInfo.token
    // useCookie('accessToken').value = accessToken
    //
    // // Redirect to `to` query if exist or redirect to index route
    // // ❗ nextTick is required to wait for DOM updates and later redirect
    await nextTick(() => {
      router.replace(route.query.to ? String(route.query.to) : '/')
    })
  }
  catch (err) {
    console.error(err)
  }
}

const onSubmit = () => {
  refVForm.value?.validate()
    .then(({ valid: isValid }) => {
      if (isValid)
        register()
    })
}
</script>

<template>
  <VContainer class="d-flex justify-center align-center" fluid style="min-height: 100vh;">
    <VRow class="d-flex justify-center">
      <VCol cols="12" md="6" lg="4">
        <VCard class="pa-5">
          <VCardTitle class="text-center"><VNodeRenderer class="text-center" :nodes="themeConfig.app.logo" /></VCardTitle>
          <VCardTitle class="text-center">註冊帳戶</VCardTitle>
          <VCardSubtitle class="text-center mb-4">
            只需要以下幾個步驟，即可完成申請！
          </VCardSubtitle>
          <VAlert
            variant="outlined"
            color="success"
            v-if="successMessage != ''"
          >
            {{ successMessage }}
          </VAlert>
          <VAlert
            variant="outlined"
            color="error"
            v-if="errorMessage != ''"
          >
            {{ errorMessage }}
          </VAlert>
          <VForm ref="refVForm"
                 @submit.prevent="onSubmit">

            <!-- 帳號(Email) -->
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="form.email"
                  label="電子信箱"
                  placeholder="請輸入您的電子信箱"
                  outlined
                  dense
                  required
                  :rules="[requiredValidator, emailValidator]"
                  :error-messages="errors.email"
                />
              </VCol>
            </VRow>

            <!-- 姓名 -->
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="form.username"
                  label="姓名"
                  placeholder="請輸入您完整的姓名(最多50字)"
                  outlined
                  dense
                  :rules="[requiredValidator, maxLengthValidator(form.username, 50)]"
                />
              </VCol>
            </VRow>

            <!-- 單位與職稱 -->
            <VRow>
              <VCol cols="12" sm="6" class="pr-sm-2">
                <AppInput
                  v-model="form.depNo"
                  label="單位"
                  type="select"
                  :remoteItem="duDepartmentSelect"
                  :remoteItemParams="{ depNo: 'Z' }"
                  item-title="label"
                  item-value="value"
                  placeholder="請選擇單位"
                  :disabled="false"
                  :rules="[requiredValidator]"
                />
              </VCol>
              <VCol cols="12" sm="6" class="pl-sm-2">
                <AppInput
                  v-model="form.postNo"
                  label="職稱"
                  type="select"
                  :remoteItem="duPositionSelect"
                  item-title="label"
                  item-value="value"
                  placeholder="請選擇職稱"
                  :disabled="false"
                  :rules="[requiredValidator]"
                />
              </VCol>
            </VRow>

            <!-- 電話 -->
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="form.phone"
                  label="電話"
                  placeholder="請輸入您的聯絡電話"
                  outlined
                  dense
                  required
                  :rules="[requiredValidator]"
                />
              </VCol>
            </VRow>

            <!-- 密碼 -->
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="form.password"
                  label="密碼"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                  :rules="[requiredValidator, passwordValidator]"
                />
              </VCol>
            </VRow>

            <!-- 確認密碼 -->
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="form.chkPassword"
                  label="確認密碼"
                  placeholder="············"
                  :type="isChkPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isChkPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isChkPasswordVisible = !isChkPasswordVisible"
                  :rules="[requiredValidator, confirmedValidator(form.chkPassword, form.password)]"
                />
              </VCol>
            </VRow>

            <!-- 同意隱私權政策 -->
            <VRow>
              <VCol cols="12">
                <VCheckbox
                  v-model="form.privacyPolicies"
                  dense
                  required
                >
                  <template v-slot:label>
                    我已閱讀並同意
                    <a href="#" class="text-decoration-none">隱私權政策</a>
                  </template>
                </VCheckbox>
              </VCol>
            </VRow>
            <!-- 註冊按鈕 -->
            <VRow>
              <VCol cols="12">
                <VBtn :disabled="!formIsValid" block color="primary" type="submit">
                  註冊
                </VBtn>
              </VCol>
            </VRow>
            <VRow>
              <VCol cols="12" class="text-center">
                <a href="/register-result" class="text-decoration-none">查詢註冊結果</a>
              </VCol>
            </VRow>
            <VRow>
              <VCol cols="12" class="text-center">
                <a href="/login" class="text-decoration-none">返回登入頁面</a>
              </VCol>
            </VRow>
          </VForm>
        </VCard>
      </VCol>
    </VRow>
  </VContainer>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";

.text-center {
  text-align: center;
}
.mb-4 {
  margin-bottom: 16px;
}
</style>
